import {
  emptyPlayerCompare,
  type PlayerCompareScene,
  type PlayerCompareSource,
  type ProjectState,
} from "@livcg/schema";
import { useEffect, useRef, useState } from "react";
import { sendRaw } from "../../../shared/sendRaw.js";
import { ConfirmDialog, cn, EmptyState, SegmentedControl, useToast } from "../../../ui/index.js";
import { useMatchDetail } from "../../useMatchDetail.js";
import { ComparePreview } from "./ComparePreview.js";
import { MappingSection } from "./MappingSection.js";
import { PlayerPicker } from "./PlayerPicker.js";
import { PlayerStatSection } from "./PlayerStatSection.js";
import {
  describeCompareError,
  type PickerRow,
  pickerRows,
  restrictToPosition,
} from "./playerCompareView.js";
import { usePlayerCompareMapping } from "./usePlayerCompareMapping.js";

type Props = { slug: string; state: ProjectState };

type SetPatch = Partial<{
  scene: PlayerCompareScene;
  source: PlayerCompareSource;
  home: string | null;
  away: string | null;
}>;

/** The "O'yinchilar" drawer — the selection goes to the extension (`playerCompare.set`), the
 *  result comes back from the replicant; putting on air is done from the "Sahna" tiles. */
export function PlayerCompareDrawer({ slug, state }: Props) {
  // Old persisted projects may not have the `playerCompare` slice.
  const pc = state.playerCompare ?? emptyPlayerCompare();
  const { toast } = useToast();
  const { detail } = useMatchDetail(state);
  const mapping = usePlayerCompareMapping(state.matchId);
  const [view, setView] = useState<"compare" | "single">("compare");
  const [confirmSnapshot, setConfirmSnapshot] = useState(false);
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  // Like is compared with like: a pick on one side limits the other side's
  // list to the same position (DEF ↔ DEF).
  const homeRows = pickerRows(detail, "home");
  const awayRows = pickerRows(detail, "away");
  const positionOf = (rows: PickerRow[], id: string | null) =>
    (id && rows.find((r) => r.id === id)?.position) || null;
  const homePosition = positionOf(homeRows, pc.home);
  const awayPosition = positionOf(awayRows, pc.away);

  const set = (patch: SetPatch) =>
    void sendRaw("playerCompare.set", { slug, ...patch }).catch((e: Error) =>
      toast({ tone: "error", title: "Yuborib bo'lmadi", description: e.message }),
    );

  // home/away selection: 250ms trailing debounce (a single useRef, leading+trailing) —
  // the first click is sent immediately (so the highlight isn't delayed), while on
  // consecutive clicks only the last selection is sent after waiting 250ms. Scene/source
  // (`set` directly) is not debounced.
  const homeAwayDebounce = useRef<{
    timer: ReturnType<typeof setTimeout> | null;
    lastSent: SetPatch | null;
  }>({ timer: null, lastSent: null });

  useEffect(
    () => () => {
      if (homeAwayDebounce.current.timer) clearTimeout(homeAwayDebounce.current.timer);
    },
    [],
  );

  const setHomeAway = (patch: SetPatch) => {
    const d = homeAwayDebounce.current;
    const leading = d.timer === null;
    if (leading) {
      d.lastSent = patch;
      set(patch);
    }
    if (d.timer) clearTimeout(d.timer);
    d.timer = setTimeout(() => {
      d.timer = null;
      if (JSON.stringify(d.lastSent) !== JSON.stringify(patch)) {
        d.lastSent = patch;
        set(patch);
      }
    }, 250);
  };

  const takeSnapshot = async () => {
    setSnapshotBusy(true);
    try {
      const res = (await sendRaw("playerCompare.snapshot", { slug })) as
        | { ok: true }
        | { ok: false; error: string; issues?: { message?: string } };
      if (!res.ok) {
        const extra = res.issues?.message ? ` — ${res.issues.message}` : "";
        toast({
          tone: "error",
          title: "Snapshot olinmadi",
          description: describeCompareError(res.error) + extra,
        });
      } else {
        toast({ tone: "success", title: "1-taym snapshot olindi" });
      }
    } catch (e) {
      toast({ tone: "error", title: "Snapshot olinmadi", description: (e as Error).message });
    } finally {
      setSnapshotBusy(false);
    }
  };

  if (mapping.status.data && !mapping.status.data.enabled) {
    return (
      <EmptyState
        title="dostats sozlanmagan"
        hint={
          "Extension .env faylida DOSTATS_CLIENT_ID / DOSTATS_CLIENT_SECRET yo'q — " +
          "Player Compare o'chiq."
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-16">
      <SegmentedControl
        size="sm"
        value={view}
        onChange={setView}
        options={[
          { value: "compare", label: "Solishtiruv" },
          { value: "single", label: "Bitta o'yinchi" },
        ]}
      />
      {view === "single" && (
        <PlayerStatSection
          slug={slug}
          state={state}
          mappedIds={mapping.mappedIds}
          mappedLoading={mapping.mappedLoading}
        />
      )}
      {view === "compare" && (
        <>
          <p className="text-[12px] text-ink-muted">
            {"Ikki o'yinchini tanlang — ko'rsatkichlar dostats'dan avtomatik hisoblanadi (Season = " +
              "joriy mavsum, Last match = o'yinchining oxirgi o'yni, Half-time = 1-taym snapshot). " +
              "Efirga chiqarish: Overlay boshqaruv → Sahna → " +
              "«O'yinchilar · pre / HT». Pre↔HT almashish efirda ~2.4 s (chiqish + kirish)."}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-ink-muted">Sahna</span>
              <SegmentedControl
                size="sm"
                value={pc.scene}
                onChange={(v) => set({ scene: v })}
                options={[
                  { value: "pre", label: "Pre-match" },
                  { value: "ht", label: "Half-time" },
                ]}
              />
            </div>
            {pc.scene === "pre" && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-ink-muted">Manba</span>
                <SegmentedControl
                  size="sm"
                  value={pc.source}
                  onChange={(v) => set({ source: v })}
                  options={[
                    { value: "season", label: "Season" },
                    { value: "last", label: "Last match" },
                  ]}
                />
              </div>
            )}
            {mapping.statusError ? (
              <span className="text-[11px] text-danger-strong">dostats: holat xatosi</span>
            ) : (
              mapping.status.data?.enabled && (
                <span className="text-[11px] text-ink-faint">
                  dostats:{" "}
                  {mapping.status.data.tokenOk
                    ? `ulangan · mavsum ${mapping.status.data.seasonId}`
                    : "token xatosi"}
                </span>
              )
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <PlayerPicker
              title={`Uy · ${state.teams.home?.shortName ?? ""}`}
              rows={restrictToPosition(homeRows, awayPosition, pc.home)}
              positionFilter={awayPosition}
              selectedId={pc.home}
              mappedIds={mapping.mappedIds}
              mappedLoading={mapping.mappedLoading}
              onSelect={(id) => setHomeAway({ home: id })}
            />
            <PlayerPicker
              title={`Mehmon · ${state.teams.away?.shortName ?? ""}`}
              rows={restrictToPosition(awayRows, homePosition, pc.away)}
              positionFilter={homePosition}
              selectedId={pc.away}
              mappedIds={mapping.mappedIds}
              mappedLoading={mapping.mappedLoading}
              onSelect={(id) => setHomeAway({ away: id })}
            />
          </div>

          <ComparePreview
            pc={pc}
            onRefresh={() =>
              void sendRaw("playerCompare.refresh", { slug }).catch((e: Error) =>
                toast({ tone: "error", title: "Yangilab bo'lmadi", description: e.message }),
              )
            }
            onSnapshot={() => (pc.htSnapshot ? setConfirmSnapshot(true) : void takeSnapshot())}
            snapshotBusy={snapshotBusy}
          />
        </>
      )}

      <details
        className="rounded-lg border border-border bg-surface p-3"
        onToggle={(e) => {
          if (e.currentTarget.open) mapping.loadFull();
        }}
      >
        <summary
          className={cn(
            "cursor-pointer text-[11px] font-semibold uppercase tracking-[0.1em]",
            "text-ink-muted",
          )}
        >
          Xaritalash (dostats)
        </summary>
        <div className="pt-3">
          <MappingSection
            mapping={mapping}
            teamLabel={(teamId) => {
              if (state.teams.home && state.teams.home.id === teamId) {
                return state.teams.home.shortName;
              }
              return state.teams.away?.shortName ?? "";
            }}
            onMapped={() => {
              if (pc.home && pc.away) {
                void sendRaw("playerCompare.refresh", { slug }).catch(() => undefined);
              }
            }}
          />
        </div>
      </details>

      <ConfirmDialog
        open={confirmSnapshot}
        title="Snapshot qayta olinsinmi?"
        description="Mavjud 1-taym snapshot joriy jonli qiymatlar bilan almashtiriladi."
        confirmLabel="Qayta olish"
        onConfirm={() => {
          setConfirmSnapshot(false);
          void takeSnapshot();
        }}
        onCancel={() => setConfirmSnapshot(false)}
      />
    </div>
  );
}
